/**
 * Ant Design theme configuration
 * Light and dark theme tokens
 */

import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';

/**
 * Light theme configuration
 */
export const lightTheme: ThemeConfig = {
  token: {
    // Primary brand color (professional blue)
    colorPrimary: '#1677ff',
    colorPrimaryHover: '#4096ff',
    colorPrimaryActive: '#0958d9',

    // Success (green for completed)
    colorSuccess: '#52c41a',
    colorSuccessHover: '#73d13d',
    colorSuccessActive: '#389e0d',

    // Warning (yellow/orange for in-progress)
    colorWarning: '#faad14',
    colorWarningHover: '#ffc53d',
    colorWarningActive: '#d48806',

    // Error (red for failed)
    colorError: '#ff4d4f',
    colorErrorHover: '#ff7875',
    colorErrorActive: '#cf1322',

    // Info (blue for pending)
    colorInfo: '#1677ff',

    // Background
    colorBgContainer: '#ffffff',
    colorBgLayout: '#f5f5f5',
    colorBgElevated: '#ffffff',
    colorBgSpotlight: '#f0f0f0',

    // Text
    colorText: '#262626',
    colorTextSecondary: '#8c8c8c',
    colorTextTertiary: '#bfbfbf',
    colorTextQuaternary: '#d9d9d9',

    // Borders
    colorBorder: '#d9d9d9',
    colorBorderSecondary: '#f0f0f0',

    // Border radius
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    borderRadiusXS: 2,

    // Font
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14,
    fontSizeSM: 12,
    fontSizeLG: 16,
    fontSizeXL: 20,

    // Line height
    lineHeight: 1.5714285714285714,

    // Control height
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 24,

    // Motion
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',

    // Box shadow
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
    boxShadowSecondary: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
  },
  components: {
    Layout: {
      siderBg: '#001529',
      headerBg: '#ffffff',
      bodyBg: '#f5f5f5',
      headerPadding: '0 24px',
      headerHeight: 64,
    },
    Menu: {
      darkItemBg: '#001529',
      darkItemSelectedBg: '#1677ff',
      darkSubMenuItemBg: '#000c17',
      darkItemHoverBg: 'rgba(255, 255, 255, 0.08)',
      itemHeight: 40,
      iconSize: 16,
    },
    Table: {
      headerBg: '#fafafa',
      headerColor: '#262626',
      rowHoverBg: '#f5f5f5',
      borderColor: '#f0f0f0',
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },
    Card: {
      headerBg: '#ffffff',
      paddingLG: 24,
    },
    Button: {
      primaryShadow: '0 2px 0 rgba(5, 145, 255, 0.1)',
    },
    Input: {
      activeBorderColor: '#1677ff',
      hoverBorderColor: '#4096ff',
    },
    Select: {
      optionSelectedBg: '#e6f4ff',
    },
    Tabs: {
      itemSelectedColor: '#1677ff',
      inkBarColor: '#1677ff',
    },
    Progress: {
      defaultColor: '#1677ff',
    },
    Tag: {
      defaultBg: '#fafafa',
    },
    Statistic: {
      contentFontSize: 24,
      titleFontSize: 14,
    },
  },
};

/**
 * Dark theme configuration
 */
export const darkTheme: ThemeConfig = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    // Primary brand color
    colorPrimary: '#1677ff',
    colorPrimaryHover: '#4096ff',
    colorPrimaryActive: '#0958d9',

    // Status colors
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    colorInfo: '#1677ff',

    // Dark backgrounds
    colorBgContainer: '#141414',
    colorBgLayout: '#000000',
    colorBgElevated: '#1f1f1f',
    colorBgSpotlight: '#2a2a2a',

    // Light text on dark
    colorText: 'rgba(255, 255, 255, 0.85)',
    colorTextSecondary: 'rgba(255, 255, 255, 0.45)',
    colorTextTertiary: 'rgba(255, 255, 255, 0.25)',
    colorTextQuaternary: 'rgba(255, 255, 255, 0.15)',

    // Borders
    colorBorder: '#424242',
    colorBorderSecondary: '#303030',

    // Border radius
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,

    // Font
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14,

    // Box shadow for dark mode
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3), 0 1px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    boxShadowSecondary: '0 6px 16px 0 rgba(0, 0, 0, 0.32), 0 3px 6px -4px rgba(0, 0, 0, 0.48), 0 9px 28px 8px rgba(0, 0, 0, 0.2)',
  },
  components: {
    Layout: {
      siderBg: '#141414',
      headerBg: '#141414',
      bodyBg: '#000000',
    },
    Menu: {
      darkItemBg: '#141414',
      darkItemSelectedBg: '#1677ff',
      darkSubMenuItemBg: '#0c0c0c',
    },
    Table: {
      headerBg: '#1f1f1f',
      headerColor: 'rgba(255, 255, 255, 0.85)',
      rowHoverBg: '#262626',
      borderColor: '#303030',
    },
    Card: {
      headerBg: '#141414',
    },
    Input: {
      colorBgContainer: '#141414',
      activeBorderColor: '#1677ff',
      hoverBorderColor: '#4096ff',
    },
    Select: {
      optionSelectedBg: '#111a2c',
    },
    Modal: {
      contentBg: '#1f1f1f',
      headerBg: '#1f1f1f',
    },
    Drawer: {
      colorBgElevated: '#1f1f1f',
    },
    Dropdown: {
      colorBgElevated: '#1f1f1f',
    },
  },
};

/**
 * Job status colors for consistent styling
 */
export const STATUS_COLORS = {
  pending: { bg: '#e6f4ff', text: '#1677ff', border: '#91caff' },
  queued: { bg: '#e6f4ff', text: '#1677ff', border: '#91caff' },
  running: { bg: '#fffbe6', text: '#d48806', border: '#ffe58f' },
  in_progress: { bg: '#fffbe6', text: '#d48806', border: '#ffe58f' },
  completed: { bg: '#f6ffed', text: '#389e0d', border: '#b7eb8f' },
  failed: { bg: '#fff2f0', text: '#cf1322', border: '#ffccc7' },
  cancelled: { bg: '#f5f5f5', text: '#8c8c8c', border: '#d9d9d9' },
  paused: { bg: '#fff7e6', text: '#d46b08', border: '#ffd591' },
} as const;

/**
 * Grid cell status colors for map visualization
 */
export const CELL_COLORS = {
  pending: '#e0e0e0',
  processing: '#ffd666',
  completed: '#95de64',
  failed: '#ff7875',
  skipped: '#d3adf7',
} as const;

/**
 * Chart color palette
 */
export const CHART_COLORS = {
  primary: '#1677ff',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  purple: '#722ed1',
  cyan: '#13c2c2',
  magenta: '#eb2f96',
  orange: '#fa8c16',
} as const;

/**
 * Get theme based on mode
 */
export function getTheme(isDark: boolean): ThemeConfig {
  return isDark ? darkTheme : lightTheme;
}

/**
 * Simple theme object for direct access to common values
 * Used by components that need theme values outside of Ant Design context
 */
export const theme = {
  colors: {
    primary: '#1677ff',
    primaryHover: '#4096ff',
    primaryActive: '#0958d9',
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
    info: '#1677ff',
    text: '#262626',
    textSecondary: '#8c8c8c',
    border: '#d9d9d9',
    background: '#f5f5f5',
  },
  borderRadius: {
    sm: 4,
    md: 6,
    lg: 8,
  },
  fonts: {
    body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
  },
} as const;
